import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ClipboardList, AlertCircle } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { legal } from "../i18n/legal";
import { Reveal } from "../components/site/Reveal";

const UI = {
  EN: {
    overline: "Internal — Legal & Compliance",
    title: "Information Required Before Publication",
    subtitle:
      "This page lists every data point marked with a placeholder [...] across the ASTEO Legal Documents Library. Each item must be confirmed by ASTEO's Legal and Compliance team before the corresponding document can be finalised and published.",
    note: "Verified data already reflected across the documents (legal entity, registered office, CSSF authorisation P00000438, LEI, CONSOB registration No 4129 and official e-mail addresses) is not listed here.",
    doc: "Document",
    back: "Legal Documents",
  },
  FR: {
    overline: "Interne — Juridique & Conformité",
    title: "Informations requises avant publication",
    subtitle:
      "Cette page recense chaque donnée marquée d'un espace réservé [...] dans la bibliothèque de documents juridiques d'ASTEO. Chaque élément doit être confirmé par l'équipe Juridique et Conformité d'ASTEO avant que le document correspondant puisse être finalisé et publié.",
    note: "Les données déjà vérifiées et intégrées dans les documents (entité juridique, siège social, agrément CSSF P00000438, LEI, enregistrement CONSOB n° 4129 et adresses e-mail officielles) ne figurent pas ici.",
    doc: "Document",
    back: "Documents juridiques",
  },
  IT: {
    overline: "Interno — Legale & Compliance",
    title: "Informazioni Necessarie Prima della Pubblicazione",
    subtitle:
      "Questa pagina elenca ogni dato contrassegnato da un segnaposto [...] nella Legal Documents Library di ASTEO. Ciascuna voce deve essere confermata dal team Legale e Compliance di ASTEO prima che il documento corrispondente possa essere finalizzato e pubblicato.",
    note: "I dati già verificati e riportati nei documenti (entità giuridica, sede legale, autorizzazione CSSF P00000438, LEI, iscrizione CONSOB n. 4129 e indirizzi e-mail ufficiali) non sono elencati qui.",
    doc: "Documento",
    back: "Documenti Legali",
  },
};

// Missing data points grouped by document slug. Each item is localised.
const CHECKLIST = [
  { slug: "order-execution", items: [
    { EN: "Actual order execution model (e.g. RTO to third brokers, STP, market maker)", FR: "Modèle d'exécution des ordres effectif (p. ex. RTO vers courtiers tiers, STP, market maker)", IT: "Modello di esecuzione degli ordini effettivo (es. RTO verso broker terzi, STP, market maker)" },
    { EN: "Execution venues and intermediaries relied upon", FR: "Lieux d'exécution et intermédiaires utilisés", IT: "Sedi di esecuzione e intermediari utilizzati" },
    { EN: "Classes of financial instruments actually handled", FR: "Classes d'instruments financiers effectivement traitées", IT: "Classi di strumenti finanziari effettivamente trattate" },
    { EN: "Whether requoting applies", FR: "Applicabilité de la requotation", IT: "Applicabilità della riquotazione" },
  ]},
  { slug: "leverage-margin", items: [
    { EN: "Maximum leverage per instrument class", FR: "Levier maximal par classe d'instrument", IT: "Leva massima per classe di strumento" },
    { EN: "Initial margin requirements", FR: "Exigences de marge initiale", IT: "Requisiti di margine iniziale" },
    { EN: "Maintenance margin level", FR: "Niveau de marge de maintien", IT: "Livello di margine di mantenimento" },
    { EN: "Margin call level", FR: "Niveau de margin call", IT: "Livello di margin call" },
    { EN: "Stop out level", FR: "Niveau de stop out", IT: "Livello di stop out" },
  ]},
  { slug: "charges-fees", items: [
    { EN: "Spreads per instrument", FR: "Spreads par instrument", IT: "Spread per strumento" },
    { EN: "Commission schedule", FR: "Barème des commissions", IT: "Tariffario delle commissioni" },
    { EN: "Minimum deposit and deposit costs by method", FR: "Dépôt minimum et coûts de dépôt par méthode", IT: "Deposito minimo e costi di deposito per metodo" },
    { EN: "Withdrawal fees", FR: "Frais de retrait", IT: "Costi di prelievo" },
    { EN: "Currency conversion margin", FR: "Marge de conversion de devises", IT: "Margine di conversione valutaria" },
    { EN: "Swap / overnight policy and rates", FR: "Politique et taux swap / overnight", IT: "Politica e tassi swap / overnight" },
    { EN: "Inactivity fee (if any)", FR: "Frais d'inactivité (le cas échéant)", IT: "Costi di inattività (se previsti)" },
    { EN: "Platform / market-data costs", FR: "Coûts plateforme / données de marché", IT: "Costi piattaforma / dati di mercato" },
  ]},
  { slug: "client-categorisation", items: [
    { EN: "Client categories actually offered by ASTEO", FR: "Catégories de clientèle effectivement proposées par ASTEO", IT: "Categorie di clientela effettivamente offerte da ASTEO" },
  ]},
  { slug: "fatca-crs", items: [
    { EN: "Applicability of FATCA/CRS reporting obligations to ASTEO and specific arrangements", FR: "Applicabilité des obligations de déclaration FATCA/CRS à ASTEO et modalités spécifiques", IT: "Applicabilità degli obblighi di comunicazione FATCA/CRS ad ASTEO e modalità specifiche" },
  ]},
  { slug: "pep", items: [
    { EN: "Detailed legal references for PEP due diligence", FR: "Références légales détaillées pour la vigilance PPE", IT: "Riferimenti normativi di dettaglio per la verifica PEP" },
  ]},
  { slug: "fraud-warning", items: [
    { EN: "Official phone number", FR: "Numéro de téléphone officiel", IT: "Numero di telefono ufficiale" },
    { EN: "Official support channel (e.g. in-app chat)", FR: "Canal d'assistance officiel (p. ex. chat in-app)", IT: "Canale di assistenza ufficiale (es. chat in-app)" },
  ]},
  { slug: "cookie", items: [
    { EN: "List of third-party providers setting cookies", FR: "Liste des fournisseurs tiers déposant des cookies", IT: "Elenco dei fornitori terzi che installano cookie" },
    { EN: "Full list of cookies (from consent manager)", FR: "Liste complète des cookies (via gestionnaire de consentement)", IT: "Elenco completo dei cookie (dal consent manager)" },
  ]},
  { slug: "complaints", items: [
    { EN: "Competent Italian ADR body for retail clients", FR: "Organisme ADR italien compétent pour les clients de détail", IT: "Organismo ADR italiano competente per i clienti al dettaglio" },
  ]},
];

export default function ComplianceChecklist() {
  const { lang } = useLang();
  const t = UI[lang];
  const L = legal[lang];

  useEffect(() => {
    document.title = `${t.title} — ASTEO LUXEMBOURG S.A.`;
    return () => {
      document.title = "ASTEO LUXEMBOURG S.A. — Regulated European CFD Broker & Family Office";
    };
  }, [t]);

  const totalItems = CHECKLIST.reduce((n, c) => n + c.items.length, 0);

  return (
    <div data-testid="compliance-checklist" className="bg-white min-h-screen">
      <section className="relative bg-slate-900 pt-[136px] pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "64px 64px" }} />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10">
          <Link data-testid="checklist-back" to="/legal" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-slate-400 hover:text-emerald-400 transition-colors duration-300">
            <ArrowLeft size={14} /> {t.back}
          </Link>
          <span className="mt-8 inline-flex items-center gap-3 text-xs uppercase tracking-[0.28em] font-mono font-medium text-amber-400">
            <ClipboardList size={14} /> {t.overline}
          </span>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white leading-[1.08]" data-testid="checklist-title">
            {t.title}
          </motion.h1>
          <p className="mt-6 text-base text-slate-400 leading-relaxed max-w-2xl">{t.subtitle}</p>
          <p className="mt-8 text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500">{totalItems} items</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <Reveal>
          <div className="border border-amber-700/40 bg-amber-50 p-5 lg:p-6 flex gap-4 mb-12">
            <AlertCircle size={20} className="text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm leading-relaxed text-amber-900/80">{t.note}</p>
          </div>
        </Reveal>

        {CHECKLIST.map((group, gi) => (
          <Reveal key={group.slug} delay={0.02 * gi}>
            <section data-testid={`checklist-group-${group.slug}`} className="pb-10 mb-10 border-b border-slate-100 last:border-0">
              <div className="flex items-baseline justify-between gap-4">
                <Link to={`/legal/${group.slug}`} className="font-serif text-lg md:text-xl font-semibold text-slate-900 hover:text-emerald-700 transition-colors duration-300">
                  {L.docs[group.slug]?.title}
                </Link>
                <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-slate-400 shrink-0">{group.items.length}</span>
              </div>
              <ul className="mt-5 space-y-3">
                {group.items.map((item, i) => (
                  <li key={i} data-testid={`checklist-item-${group.slug}-${i}`} className="flex items-start gap-3 text-sm text-slate-600 leading-relaxed">
                    <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                    {item[lang]}
                  </li>
                ))}
              </ul>
            </section>
          </Reveal>
          ))}
        </div>
    </div>
  );
}
